import { stripDisabledNameFields, toaster } from '@welbe/design-system';
import { GENERIC_CURP } from 'constants/genericNationalIdentity';
import { BrokersContext } from 'contexts/Brokers/Brokers';
import Analytics from 'helpers/analytics';
import { mapBiologicalSexToCurpSex } from 'helpers/curp';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
	getCountriesCatalog,
	getFederalEntitiesCatalog,
	postValidateCurp,
} from 'services/services';
import { sanitize } from 'utils/sanitize';
import { formatErrorResponse, validate } from 'utils/zodValidation';
import {
	postEmployee,
	putEmployee,
} from 'Views/Employees/services/EmployeesServices';
import { EmployeeSchema } from './Employee.schema';
import type { EmployeeProps, OptionType } from './Employee.types';

const MEXICO_COUNTRY_CODE = 142;
const DEFAULT_CURP_INVALID = 'CURP inválido';

type EmployeeProviderProps = { children: React.ReactNode };

type EmployeeContextType = {
	errors: string[];
	fieldErrors: Record<string, any>;
	formLoading: boolean;
	employeeForm: EmployeeProps;
	setEmployeeForm: (employeeForm: any) => void;
	loadingCountries: boolean;
	methods: ReturnType<typeof useForm<Inputs>>;
	countries: OptionType[];
	loadingFederalEntities: boolean;
	federalEntities: OptionType[];
	onSubmit: () => void;
	onCancel: () => void;
};

interface Inputs {
	firstName?: string;
	middleName?: string;
	lastName?: string;
	hasNoMiddleName?: boolean;
	hasNoLastName?: boolean;
	hasGenericNationalIdentity?: boolean;
	birthDate?: string;
	nationalIdentity?: string;
	email?: string;
	phone?: string;
	alternativeEmail?: string;
	alternativePhone?: string;
	socialSecurityNumber?: string;
	companyId?: string;
	companyFancyName?: string;
	branchOfficeId?: string;
	branchOfficeName?: string;
	companyBadgeNumber?: string;
	corporateEmail?: string;
	hiringDate?: string | null;
	sector?: string;
	shift?: string;
	position?: string;
	riskProfiles?: string[];
	expositionAreas?: string[];
	insuranceId?: string;
	siresProfile?: {
		countryCode?: string;
		federalEntityCode?: string | number;
		nationalityCode?: string;
		nationalIdentityGender?: string;
		biologicalSex?: string;
		gender?: string;
		inheritanceRights?: string[];
		regionalInfo?: {
			country?: string | number;
			federalEntity?: string | number;
			municipality?: string | number;
			location?: string | number;
		};
	};
}

export const EmployeeContext = createContext<EmployeeContextType>(
	{} as EmployeeContextType,
);

const changeStringToNumber = (value: string) => {
	if (!value) return undefined;
	if (typeof value === 'number') return value;

	return Number(value);
};

export const EmployeeProvider = ({ children }: EmployeeProviderProps) => {
	const navigate = useNavigate();
	const { isBroker } = useContext(BrokersContext);
	const [formLoading, setFormLoading] = useState(false);
	const [errors, setErrors] = useState<string[]>([]);
	const [fieldErrors, setFieldErrors] = useState<Record<string, any>>({});
	const [loadingCountries, setLoadingCountries] = useState(false);
	const [countries, setCountries] = useState([]);

	const [loadingFederalEntities, setLoadingFederalEntities] = useState(false);
	const [federalEntities, setFederalEntities] = useState([]);

	const [employeeForm, setEmployeeForm] = useState<EmployeeProps>({
		siresProfile: {
			regionalInfo: {
				country: undefined,
				federalEntity: undefined,
				municipality: undefined,
				location: undefined,
			},
		},
	});

	const methods = useForm<Inputs>({
		values: {
			firstName: employeeForm.firstName || '',
			middleName: employeeForm.middleName || '',
			lastName: employeeForm.lastName || '',
			hasNoMiddleName: employeeForm.hasNoMiddleName ?? false,
			hasNoLastName: employeeForm.hasNoLastName ?? false,
			hasGenericNationalIdentity:
				employeeForm.hasGenericNationalIdentity ?? false,
			birthDate: employeeForm.birthDate || '',
			nationalIdentity: employeeForm.nationalIdentity || '',
			email: employeeForm.email || '',
			phone: employeeForm.phone || '',
			alternativeEmail: employeeForm.alternativeEmail || '',
			alternativePhone: employeeForm.alternativePhone || '',
			socialSecurityNumber: employeeForm.socialSecurityNumber || '',
			companyId: employeeForm.companyId || '',
			companyFancyName: employeeForm.companyFancyName || '',
			branchOfficeId: employeeForm.branchOfficeId || '',
			branchOfficeName: employeeForm.branchOfficeName || '',
			companyBadgeNumber: employeeForm.companyBadgeNumber || '',
			corporateEmail: employeeForm.corporateEmail || '',
			hiringDate: employeeForm.hiringDate || '',
			sector: employeeForm.sector || '',
			shift: employeeForm.shift || '',
			position: employeeForm.position || '',
			riskProfiles: employeeForm.riskProfiles || [],
			expositionAreas: employeeForm.expositionAreas || [],
			insuranceId: employeeForm.insuranceId || '',
			siresProfile: {
				countryCode: employeeForm.siresProfile?.countryCode?.toString() || '',
				federalEntityCode:
					employeeForm.siresProfile?.federalEntityCode?.toString() || '',
				nationalityCode:
					employeeForm.siresProfile?.nationalityCode?.toString() || '',
				nationalIdentityGender:
					employeeForm.siresProfile?.nationalIdentityGender ||
					employeeForm?.gender?.toUpperCase() ||
					'',
				biologicalSex: employeeForm.siresProfile?.biologicalSex || '',
				gender: employeeForm.siresProfile?.gender || '',
				inheritanceRights: employeeForm.siresProfile?.inheritanceRights || [],
				regionalInfo: {
					country: employeeForm.siresProfile?.regionalInfo?.country || '',
					federalEntity:
						employeeForm.siresProfile?.regionalInfo?.federalEntity || '',
					municipality:
						employeeForm.siresProfile?.regionalInfo?.municipality || '',
					location: employeeForm.siresProfile?.regionalInfo?.location || '',
				},
			},
		},
	});

	const fetchCountries = async () => {
		try {
			setLoadingCountries(true);
			const { data } = await getCountriesCatalog();
			setCountries(
				data.content.countries.map(
					(country: { code: number; country: string }) => ({
						id: country.code.toString(),
						name: country.country,
					}),
				),
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingCountries(false);
		}
	};

	const fetchFederalEntities = async () => {
		try {
			setLoadingFederalEntities(true);
			const { data } = await getFederalEntitiesCatalog();
			setFederalEntities(
				data.content.federalEntities.map(
					(entity: { code: number; name: string }) => ({
						id: entity.code.toString(),
						name: entity.name,
					}),
				),
			);
		} catch (error) {
			console.error(error);
		} finally {
			setLoadingFederalEntities(false);
		}
	};

	const onCancel = () => {
		navigate('/employees');
	};

	const onSubmit = async () => {
		setErrors([]);
		try {
			setFormLoading(true);
			const formValues = methods.getValues();
			const isEdition = Boolean(employeeForm.id);

			const payload = {
				...formValues,
				siresProfile: {
					...formValues.siresProfile,
					countryCode: changeStringToNumber(
						formValues.siresProfile?.countryCode as any,
					),
					federalEntityCode: changeStringToNumber(
						formValues.siresProfile?.federalEntityCode as any,
					),
					nationalityCode: changeStringToNumber(
						formValues.siresProfile?.nationalityCode as any,
					),
					regionalInfo: {
						...formValues.siresProfile?.regionalInfo,
						federalEntity: changeStringToNumber(
							formValues.siresProfile?.regionalInfo?.federalEntity as any,
						),
						location: changeStringToNumber(
							formValues.siresProfile?.regionalInfo?.location as any,
						),
						municipality: changeStringToNumber(
							formValues.siresProfile?.regionalInfo?.municipality as any,
						),
					},
				},
			};

			const validated = await validate(
				EmployeeSchema,
				stripDisabledNameFields(payload),
			);

			const finalPayload = {
				...payload,
				firstName: validated.firstName,
				middleName: formValues.hasNoMiddleName ? '' : validated.middleName,
				lastName: formValues.hasNoLastName ? '' : validated.lastName,
			};

			const isMexicanBirth =
				Number(finalPayload.siresProfile?.countryCode) === MEXICO_COUNTRY_CODE;
			const curp = finalPayload.nationalIdentity;
			if (isMexicanBirth && curp && curp !== GENERIC_CURP) {
				await postValidateCurp({
					curp,
					context: {
						firstName: finalPayload.firstName ?? '',
						middleName: finalPayload.middleName,
						lastName: finalPayload.lastName,
						birthDate: finalPayload.birthDate,
						sex: mapBiologicalSexToCurpSex(
							finalPayload.siresProfile?.biologicalSex,
						),
						birthStateCode:
							typeof finalPayload.siresProfile?.federalEntityCode === 'number'
								? finalPayload.siresProfile.federalEntityCode
								: undefined,
					},
				});
			}

			if (isEdition) {
				await putEmployee(employeeForm.id || '', sanitize(finalPayload));
				Analytics.track('employee', 'Edit Employee Successful', {
					isBroker,
					employee: formValues,
				});
			} else {
				await postEmployee(sanitize(finalPayload));
				Analytics.track('employee', 'Create Employee Successful', {
					isBroker,
					employee: formValues,
				});
			}

			navigate('/employees');
			toaster.create({
				title: 'Éxito',
				description: isEdition
					? 'Empleado actualizado con éxito'
					: 'Empleado creado con éxito',
				type: 'success',
			});
		} catch (error: any) {
			const status = error?.response?.status;
			const serverMessage = error?.response?.data?.message;

			if (status === 400 && serverMessage === 'CURP validation failed') {
				const message =
					error?.response?.data?.errors?.[0]?.message ?? DEFAULT_CURP_INVALID;
				setErrors((prev) => Array.from(new Set([...prev, 'nationalIdentity'])));
				toaster.create({
					title: 'CURP inválido',
					description: message,
					type: 'error',
				});
				return;
			}

			if (error.zodError) {
				const formatted = formatErrorResponse(error.zodError);
				setErrors(Object.keys(formatted));
				setFieldErrors(formatted);
				toaster.create({
					title: 'Error',
					description: 'Llena todos los campos obligatorios',
					type: 'error',
				});
				return;
			}

			console.error('Erro ao enviar formulário:', error);
			toaster.create({
				title: 'Error',
				description: 'Hubo un error al enviar el formulario',
				type: 'error',
			});
		} finally {
			setFormLoading(false);
		}
	};

	useEffect(() => {
		fetchCountries();
		fetchFederalEntities();
	}, []);

	return (
		<EmployeeContext.Provider
			value={{
				errors,
				fieldErrors,
				methods,
				formLoading,
				employeeForm,
				setEmployeeForm,
				loadingCountries,
				countries,
				loadingFederalEntities,
				federalEntities,
				onSubmit,
				onCancel,
			}}
		>
			{children}
		</EmployeeContext.Provider>
	);
};
